import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@hooks/useAuth";
import { HiXCircle } from "react-icons/hi";
import { FiLoader } from "react-icons/fi";

export const Login: React.FC = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const { loginWithWrike, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pega a página de origem ou usa a home como padrão
  const from = (location.state as any)?.from?.pathname || "/";

  // Verificar se há erro na URL (retorno de callback com erro)
  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam) {
      switch (errorParam) {
        case "wrike_auth_failed":
          setError("Falha na autenticação com Wrike. Tente novamente.");
          break;
        case "no_code":
          setError("Código de autorização não encontrado.");
          break;
        case "callback_failed":
          setError("Erro ao processar autenticação. Tente novamente.");
          break;
        case "invalid_domain":
          setError(
            "Seu domínio de email não tem permissão para acessar o sistema."
          );
          break;
        case "token_expired":
          setError("Sua sessão expirou. Por favor, faça login novamente.");
          break;
        default:
          setError("Erro ao fazer login. Tente novamente.");
      }
    }
  }, [searchParams]);

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleWrikeLogin = async () => {
    setError("");
    setLoading(true);

    try {
      await loginWithWrike();
      // O redirecionamento será feito pelo OAuth flow
    } catch (err: any) {
      setError(err?.message || "Erro ao conectar com Wrike");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 bg-base-200">
      <div className="card bg-base-100 shadow-2xl w-full max-w-md animate-fade-in">
        <div className="card-body">
          <div className="flex flex-col items-center mb-6">
            <h1 className="text-3xl font-bold text-center mb-2 text-base-content">
              Sistema de Cotação
            </h1>
            <p className="text-center text-base-content/70">
              Faça login para continuar
            </p>
          </div>

          {error && (
            <div className="alert alert-error mb-4">
              <HiXCircle className="shrink-0 h-6 w-6" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleWrikeLogin}
            disabled={loading}
            className="btn btn-primary btn-lg w-full shadow-lg"
          >
            {loading ? (
              <>
                <FiLoader className="animate-spin h-5 w-5 mr-2" />
                Conectando ao Wrike...
              </>
            ) : (
              <>
                <svg
                  width="24"
                  height="24"
                  viewBox="-2.37 .48 605.11 378.12"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m141.07 138.77c29.02 0 42.7 5.34 63.71 26.39l112.75 112.9c3.34 3.34 4.01 4.68 4.67 6.68.34.67.34 1.67.34 2.34s0 1.67-.34 2.34c-.66 2-1.33 3.34-4.67 6.68l-77.05 77.49c-3.34 3.34-4.67 4.01-6.68 4.68-.66.33-1.66.33-2.33.33s-1.67 0-2.34-.33c-2-.67-3.33-1.34-6.67-4.68l-218.5-218.79c-6.33-6.35-4-16.03 6.68-16.03zm318.23-137.96c-29.02 0-42.7 5.34-63.71 26.39l-112.75 112.9c-3.34 3.34-4.01 4.68-4.67 6.68-.34.67-.34 1.67-.34 2.34s0 1.67.34 2.34c.66 2 1.33 3.34 4.67 6.68l77.05 77.16c3.34 3.34 4.67 4.01 6.68 4.68.66.33 1.66.33 2.33.33s1.67 0 2.34-.33c2-.67 3.33-1.34 6.67-4.68l218.49-218.79c6.34-6.35 4.01-16.03-6.67-16.03h-130.43z"
                    fill="white"
                  />
                </svg>
                Entrar com Wrike
              </>
            )}
          </button>

          <div className="mt-6 text-center text-sm text-base-content/60">
            <p>Use suas credenciais do Wrike para acessar o sistema</p>
          </div>
        </div>
      </div>
    </div>
  );
};
